<script setup lang="ts">
import type { Image } from "@/rvapi";
import { RouterLink, useRouter } from "vue-router";
import { ref, onMounted, reactive } from "vue";
import { storeToRefs } from "pinia";
import {
  edit,
  drag,
  minus,
  upload,
  trash_can,
} from "pn-design-assets/pn-assets/icons";
import ConfirmationModal from "../modals/ConfirmationModal.vue";
import { useImageStore } from "@/stores/images";

const imageStore = useImageStore();
const { images, categories } = storeToRefs(imageStore);
const router = useRouter();
const dragsource = ref("");
const dragtarget = ref("");
const loaded = ref(false);
const selectedcat = ref(0);
const showSpinner = ref(false);
const showDeleteConfirmationModal = ref(false);
const deleteImageHeading = ref("");
let selectedImage: Image | null = null;
const initialDeleteStatusObj = {
  isDisplayMessage: false,
  action: "",
  status: "",
  message: "",
};
const deleteImageStatusObj = reactive({ ...initialDeleteStatusObj });
const setcategory = async (e: { target: { value: string } }) => {
  imageStore.selectedImageCategory = parseInt(e.target.value);
  selectedcat.value = imageStore.selectedImageCategory;
  await refresh();
};
const refresh = async () => {
  await imageStore.getImages(selectedcat.value);
};
onMounted(async () => {
  await imageStore.getImageCategories();
  loaded.value = true;
  selectedcat.value =
    imageStore.selectedImageCategory === 0
      ? categories.value[0].categoryId
      : imageStore.selectedImageCategory;
  await refresh();
});
const dragenter = (e: any) => {
  e.preventDefault();
};
const dragover = (e: any) => {
  dragtarget.value = e.target.closest("tr").dataset.id;

  e.target.closest("tr").classList.add("target");
  e.preventDefault();
};
const dragleave = (e: any) => {
  e.target.closest("tr").classList.remove("target");
};
const dragstart = (e: any) => {
  dragsource.value = e.target.closest("tr").dataset.id;
};
const drop = async (e: any) => {
  e.target.closest("tr").classList.remove("target");
  const sourceIx = images.value.findIndex((c) => c._id === dragsource.value);
  const source = images.value.splice(sourceIx, 1)[0];
  if (dragtarget.value === "last") {
    images.value.push(source);
  } else {
    const targetIx = images.value.findIndex((c) => c._id === dragtarget.value);
    images.value.splice(targetIx, 0, source);
  }

  dragsource.value = "";
  dragtarget.value = "";
  await imageStore.reorderImages(images.value.map((c) => c._id));
};

const handleDeleteConfirmationShow = (image: Image) => {
  reset();
  showDeleteConfirmationModal.value = true;
  selectedImage = image;
  deleteImageHeading.value = `Are you sure you want to delete the image with code ${image.code}?`;
};

const handleEditButtonClick = (image: Image) => {
  router.push(`/images/${image._id}`);
};

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
};

const deleteImage = async () => {
  try {
    showSpinner.value = true;
    await imageStore.deleteCollectionImage(selectedImage?._id ?? "");
    await imageStore.getImages(selectedcat.value);
    displayNotification("success");
  } catch {
    displayNotification("error");
    console.log("Error in deleting collection Image");
  } finally {
    showDeleteConfirmationModal.value = false;
    showSpinner.value = false;
    window.scrollTo(0, 0);
  }
};

const displayNotification = (status: string) => {
  deleteImageStatusObj.isDisplayMessage = true;
  if (status === "success") {
    deleteImageStatusObj.status = "success";
    deleteImageStatusObj.message = "Collection Image deleted successfully";
  } else if (status === "error") {
    deleteImageStatusObj.status = "error";
    deleteImageStatusObj.message =
      "There was an error in deleting the collection image";
  }
};

const reset = (): void => {
  Object.assign(deleteImageStatusObj, initialDeleteStatusObj);
  selectedImage = null;
};
</script>

<template>
  <h1>Images</h1>
  <pn-toast
    v-if="deleteImageStatusObj.isDisplayMessage"
    :appearance="deleteImageStatusObj.status"
    :text="deleteImageStatusObj.message"
  ></pn-toast>
  <p>
    <RouterLink class="pnbutton" :to="`/uploadimages`"
      >Upload more images <pn-icon :icon="upload" color="blue700" small="true"
    /></RouterLink>
  </p>
  <div v-show="!loaded">
    <pn-spinner size="4" />
  </div>
  <div v-show="loaded">
    <h3>Category</h3>
    <p>
      <pn-select @change="setcategory" label="Select category">
        <option
          v-for="c in categories"
          :key="c.categoryId"
          :selected="c.categoryId === selectedcat"
          :value="c.categoryId"
        >
          {{ c.swedish || c.english || c.danish || c.norwegian }}
        </option>
      </pn-select>
    </p>
  </div>
  <div v-show="loaded && selectedcat">
    <pn-table bordered="true" striped="true">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Languages</th>
            <th>Code</th>
            <th colspan="3" class="action">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="c in images"
            :key="c._id"
            :data-id="c._id"
            draggable="true"
            @dragstart="dragstart"
            @drop="drop"
            @dragenter="dragenter"
            @dragleave="dragleave"
            @dragover="dragover"
            :class="c.hidden ? 'hidden' : ''"
          >
            <td><img class="thumb" :src="c.thumbUrl" /></td>
            <td class="flags">
              <span v-show="c.languages.includes('swedish')">
                <img src="/se.svg" />
              </span>
              <span v-show="c.languages.includes('english')">
                <img src="/gb.svg" />
              </span>
              <span v-show="c.languages.includes('danish')">
                <img src="/dk.svg" />
              </span>
              <span v-show="c.languages.includes('norwegian')">
                <img src="/no.svg" />
              </span>
            </td>
            <td>{{ c.code }}</td>
            <td>
              <pn-button
                small
                variant="outlined"
                appearance="light"
                :icon="edit"
                tooltip="Edit"
                @click="handleEditButtonClick(c)"
              />
            </td>
            <td>
              <pn-button
                small
                variant="outlined"
                appearance="light"
                :icon="trash_can"
                tooltip="Delete"
                @click="handleDeleteConfirmationShow(c)"
              />
            </td>
            <td>
              <pn-icon :icon="drag" color="blue700">Drag</pn-icon>
            </td>
          </tr>
          <tr>
            <td
              data-id="last"
              style="text-align: center"
              colspan="3"
              @drop="drop"
              @dragenter="dragenter"
              @dragleave="dragleave"
              @dragover="dragover"
            >
              <pn-icon :icon="minus" color="blue700" />
            </td>

            <td>
              <RouterLink class="pnbutton small icononly" :to="`/uploadimages`">
                <pn-icon :icon="upload" color="blue700" small="true" />
              </RouterLink>
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </pn-table>
    <ConfirmationModal
      :showLoader="showSpinner"
      :open="showDeleteConfirmationModal"
      :heading="deleteImageHeading"
      @onCancel="closeDeleteConfirmationModal"
      @onConfirm="deleteImage"
    />
  </div>
</template>

<style lang="scss" scoped>
pn-table,
pn-table .pn-table-container table {
  width: unset;
}
pn-table pn-button {
  margin-right: 1rem;
}
.hidden {
  filter: saturate(0);
  text-decoration: line-through;
}

.thumb {
  width: 100px;
}
.target {
  border-top: 3px dashed gray;
}

td > pn-icon {
  padding-bottom: 0.3rem;
}

div.grid-two-columns {
  display: grid;
  grid-template-columns: 0.5fr 1fr;
  column-gap: 1rem;
  margin-top: 0;
}

.flags > span {
  margin-left: 5px;
}
.flags img {
  border: 0;
  border-radius: 0;
  width: 20px;
}

img {
  border: 1px dashed gray;
  border-radius: 1rem;
  width: 300px;
}
.icon {
  width: 200px;
}
.dist {
  margin-top: 1.5rem;
}
.action {
  text-align: center;
}
</style>
